import React, { useState, useEffect } from 'react';
import { FolderOpen, X, Plus, Trash2, Edit, Users, RefreshCw, Loader2, Save, XCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { projectsAPI, usersAPI } from '../services/api';

const ProjectsPage = ({ onClose, onProjectCreated, onProjectDeleted }) => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDescription, setNewProjectDescription] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [allUsers, setAllUsers] = useState([]);
  const [projectUsers, setProjectUsers] = useState({});
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedUserRole, setSelectedUserRole] = useState('admin');
  const [selectedProjectForUser, setSelectedProjectForUser] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchProjectUsers = async (projectId, forceRefresh = false) => {
    if (!forceRefresh && projectUsers[projectId] !== undefined) {
      return; // Already loaded
    }
    try {
      const response = await projectsAPI.getUsers(projectId);
      setProjectUsers(prev => ({ ...prev, [projectId]: response.data || [] }));
    } catch (error) {
      console.error(`Error fetching users for project ${projectId}:`, error);
      setProjectUsers(prev => ({ ...prev, [projectId]: [] }));
    }
  };

  const toggleProjectExpansion = (projectId) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectId)) {
      newExpanded.delete(projectId);
    } else {
      newExpanded.add(projectId);
      // Load users when expanding
      fetchProjectUsers(projectId);
    }
    setExpandedProjects(newExpanded);
  };

  const handleAddUserToProject = async (e, projectId) => {
    e.preventDefault();
    if (!selectedUserId || !selectedUserRole) {
      setStatus('يرجى اختيار المستخدم والدور');
      return;
    }

    try {
      setStatus('جاري إضافة المستخدم...');
      await projectsAPI.addUser(projectId, selectedUserId, selectedUserRole);
      
      const userId = parseInt(selectedUserId);
      const user = allUsers.find(u => u.id === userId);
      
      // Clear selection
      setSelectedUserId('');
      setSelectedUserRole('admin');
      setSelectedProjectForUser(null);
      
      // Refresh project users
      await fetchProjectUsers(projectId, true);
      
      // Optimistically update local state
      if (user) {
        setProjectUsers(prev => {
          const currentUsers = prev[projectId] || [];
          if (currentUsers.some(u => u.id === userId)) {
            return prev; // Already added
          }
          return {
            ...prev,
            [projectId]: [...currentUsers, {
              id: user.id,
              username: user.username,
              role: user.role,
              project_role: selectedUserRole
            }]
          };
        });
      }
      
      setStatus(`تم إضافة المستخدم "${user?.username || selectedUserId}" إلى المشروع بنجاح!`);
    } catch (error) {
      console.error('Error adding user to project:', error);
      setStatus(error.response?.data?.error || 'فشل في إضافة المستخدم إلى المشروع');
    }
  };

  const handleRemoveUserFromProject = async (projectId, userId) => {
    const project = projects.find(p => p.id === projectId);
    const user = projectUsers[projectId]?.find(u => u.id === userId);
    if (!window.confirm(`هل أنت متأكد من إزالة المستخدم "${user?.username}" من المشروع "${project?.name}"؟`)) {
      return;
    }

    try {
      setStatus('جاري إزالة المستخدم...');
      await projectsAPI.removeUser(projectId, userId);
      
      // Update local state
      setProjectUsers(prev => ({
        ...prev,
        [projectId]: (prev[projectId] || []).filter(u => u.id !== userId)
      }));
      
      setStatus(`تم إزالة المستخدم من المشروع بنجاح!`);
      
      // Refresh from server
      setTimeout(() => {
        fetchProjectUsers(projectId, true);
      }, 100);
    } catch (error) {
      console.error('Error removing user from project:', error);
      setStatus(error.response?.data?.error || 'فشل في إزالة المستخدم من المشروع');
    }
  };

  const fetchProjects = async (retryCount = 0) => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      const projects = (response.data || []).filter(p => p.is_active !== false);
      setProjects(projects);
      console.log('✅ Projects fetched successfully:', projects.length);
    } catch (error) {
      console.error('❌ Error fetching projects:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Retry logic for production
      if (retryCount < 2) {
        console.log(`🔄 Retrying fetchProjects (attempt ${retryCount + 1})...`);
        setTimeout(() => fetchProjects(retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      
      setStatus('خطأ في تحميل المشاريع. يرجى تحديث الصفحة.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    if (!newProjectName.trim()) {
      setStatus('يرجى إدخال اسم المشروع');
      return;
    }

    try {
      setStatus('جاري إنشاء المشروع...');
      const response = await projectsAPI.create({ 
        name: newProjectName, 
        description: newProjectDescription 
      });
      
      let newProject;
      if (response.data && response.data.project) {
        newProject = { ...response.data.project };
      } else if (response.data && response.data.id) {
        newProject = {
          id: response.data.id,
          name: newProjectName,
          description: newProjectDescription,
          is_active: true,
          created_at: new Date().toISOString()
        };
      } else {
        newProject = {
          id: response.data?.id || Date.now(),
          name: newProjectName,
          description: newProjectDescription,
          is_active: true,
          created_at: new Date().toISOString()
        };
      }

      // Optimistically add to list
      setProjects(prev => [newProject, ...prev].sort((a, b) => a.name.localeCompare(b.name)));
      
      setStatus(`تم إنشاء المشروع "${newProjectName}" بنجاح!`);
      setNewProjectName('');
      setNewProjectDescription('');
      
      // Notify parent if callback provided
      if (onProjectCreated) {
        onProjectCreated(newProject);
      }
      
      // Refresh from server after a short delay
      // Use longer delay in production to ensure database commit
      setTimeout(() => {
        fetchProjects().catch(err => {
          console.error('Error refreshing projects after creation:', err);
          // Keep optimistic update even if refresh fails
        });
      }, 500);
    } catch (error) {
      console.error('Error creating project:', error);
      setStatus(error.response?.data?.error || 'فشل في إنشاء المشروع');
      // Remove optimistically added project on error
      setProjects(prev => prev.filter(p => p.id !== newProject?.id));
    }
  };

  const handleEditProject = (project) => {
    setEditingProject(project.id);
    setEditName(project.name);
    setEditDescription(project.description || '');
  };

  const handleSaveEdit = async (projectId) => {
    if (!editName.trim()) {
      setStatus('يرجى إدخال اسم المشروع');
      return;
    }

    try {
      setStatus('جاري تحديث المشروع...');
      await projectsAPI.update(projectId, {
        name: editName,
        description: editDescription
      });

      // Update in list
      setProjects(prev => prev.map(p => 
        p.id === projectId 
          ? { ...p, name: editName, description: editDescription }
          : p
      ));

      setStatus('تم تحديث المشروع بنجاح!');
      setEditingProject(null);
      setEditName('');
      setEditDescription('');
    } catch (error) {
      console.error('Error updating project:', error);
      setStatus(error.response?.data?.error || 'فشل في تحديث المشروع');
    }
  };

  const handleDeleteProject = async (projectId) => {
    const project = projects.find(p => p.id === projectId);
    if (!window.confirm(`هل أنت متأكد من حذف المشروع "${project?.name}"؟\n\nسيتم حذف جميع البيانات المرتبطة بهذا المشروع.`)) {
      return;
    }

    try {
      setStatus('جاري حذف المشروع...');
      await projectsAPI.delete(projectId);
      
      setProjects(prev => prev.filter(p => p.id !== projectId));
      setStatus(`تم حذف المشروع "${project?.name}" بنجاح!`);
      
      // Notify parent if callback provided
      if (onProjectDeleted) {
        onProjectDeleted(projectId);
      }
      
      setTimeout(() => {
        fetchProjects();
      }, 100);
    } catch (error) {
      console.error('Error deleting project:', error);
      setStatus(error.response?.data?.error || 'فشل في حذف المشروع');
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-3xl font-bold text-gray-800 flex items-center">
              <FolderOpen className="w-8 h-8 mr-3 text-indigo-600" />
              إدارة المشاريع
            </h1>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition"
                title="إغلاق"
              >
                <X className="w-6 h-6" />
              </button>
            )}
          </div>

          {status && (
            <div className={`mb-4 p-3 rounded-lg ${
              status.includes('نجاح') || status.includes('تم')
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            }`}>
              {status}
            </div>
          )}

          {/* Create Project Form */}
          <div className="bg-indigo-50 p-6 rounded-lg mb-6 border border-indigo-200">
            <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center">
              <Plus className="w-5 h-5 mr-2 text-indigo-600" />
              إضافة مشروع جديد
            </h2>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  اسم المشروع *
                </label>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(e) => setNewProjectName(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  وصف المشروع
                </label>
                <textarea
                  value={newProjectDescription}
                  onChange={(e) => setNewProjectDescription(e.target.value)}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  rows="3"
                />
              </div>
              <button
                type="submit"
                className="bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700 transition flex items-center"
              >
                <Plus className="w-4 h-4 mr-2" />
                إضافة مشروع
              </button>
            </form>
          </div>

          {/* Projects List */}
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <FolderOpen className="w-5 h-5 mr-2 text-indigo-600" />
                قائمة المشاريع ({projects.length})
              </h2>
              <button
                onClick={fetchProjects}
                className="flex items-center text-sm text-indigo-600 hover:text-indigo-800"
                title="تحديث"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                تحديث
              </button>
            </div>

            {loading ? (
              <div className="flex justify-center items-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : projects.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                لا توجد مشاريع حالياً
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        اسم المشروع
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الوصف
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        تاريخ الإنشاء
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        المستخدمون
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        الإجراءات
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {projects.map((project) => {
                      const projectUsersList = projectUsers[project.id] || [];
                      const isExpanded = expandedProjects.has(project.id);
                      
                      return (
                        <React.Fragment key={project.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              {editingProject === project.id ? (
                                <input
                                  type="text"
                                  value={editName}
                                  onChange={(e) => setEditName(e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                />
                              ) : (
                                <div className="font-semibold text-gray-900">{project.name}</div>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              {editingProject === project.id ? (
                                <textarea
                                  value={editDescription}
                                  onChange={(e) => setEditDescription(e.target.value)}
                                  className="w-full p-2 border border-gray-300 rounded focus:ring-indigo-500 focus:border-indigo-500"
                                  rows="2"
                                />
                              ) : (
                                <div className="text-gray-600">{project.description || '-'}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {project.created_at 
                                ? new Date(project.created_at).toLocaleDateString('ar-SA')
                                : '-'
                              }
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <button
                                onClick={() => toggleProjectExpansion(project.id)}
                                className="flex items-center text-indigo-600 hover:text-indigo-800"
                                title={isExpanded ? 'إخفاء المستخدمين' : 'عرض المستخدمين'}
                              >
                                <Users className="w-5 h-5 mr-1" />
                                {projectUsersList.length}
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4 mr-1" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 mr-1" />
                                )}
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {editingProject === project.id ? (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleSaveEdit(project.id)}
                                    className="text-green-600 hover:text-green-800"
                                    title="حفظ"
                                  >
                                    <Save className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => {
                                      setEditingProject(null);
                                      setEditName('');
                                      setEditDescription('');
                                    }}
                                    className="text-red-600 hover:text-red-800"
                                    title="إلغاء"
                                  >
                                    <XCircle className="w-5 h-5" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleEditProject(project)}
                                    className="text-indigo-600 hover:text-indigo-800"
                                    title="تعديل"
                                  >
                                    <Edit className="w-5 h-5" />
                                  </button>
                                  <button
                                    onClick={() => handleDeleteProject(project.id)}
                                    className="text-red-600 hover:text-red-800"
                                    title="حذف"
                                  >
                                    <Trash2 className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan="5" className="px-6 py-4 bg-gray-50">
                                <div className="space-y-4">
                                  {/* Add User to Project Form */}
                                  <div className="bg-white p-4 rounded-lg border border-indigo-200">
                                    <h5 className="text-lg font-bold text-indigo-800 mb-3 flex items-center">
                                      <Plus className="w-4 h-4 mr-2" /> إضافة مستخدم للمشروع
                                    </h5>
                                    <form onSubmit={(e) => handleAddUserToProject(e, project.id)} className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                      <select
                                        value={selectedProjectForUser === project.id ? selectedUserId : ''}
                                        onChange={(e) => {
                                          setSelectedProjectForUser(project.id);
                                          setSelectedUserId(e.target.value);
                                          const user = allUsers.find(u => u.id === parseInt(e.target.value));
                                          if (user) {
                                            setSelectedUserRole(user.role);
                                          }
                                        }}
                                        className="p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                      >
                                        <option value="">اختر المستخدم</option>
                                        {allUsers
                                          .filter(u => u.role !== 'superadmin' && !u.is_blocked && !u.is_removed)
                                          .filter(u => !projectUsersList.some(pu => pu.id === u.id))
                                          .map(user => (
                                            <option key={user.id} value={user.id}>
                                              {user.username} ({user.role === 'admin' ? 'مدير' : 'بائع'})
                                            </option>
                                          ))}
                                      </select>
                                      <select
                                        value={selectedProjectForUser === project.id ? selectedUserRole : 'admin'}
                                        onChange={(e) => {
                                          setSelectedProjectForUser(project.id);
                                          setSelectedUserRole(e.target.value);
                                        }}
                                        className="p-3 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                                        disabled={!selectedUserId || selectedProjectForUser !== project.id}
                                      >
                                        <option value="admin">مدير</option>
                                        <option value="vendor">بائع</option>
                                      </select>
                                      <button
                                        type="submit"
                                        disabled={!selectedUserId || selectedProjectForUser !== project.id}
                                        className="md:col-span-2 bg-indigo-600 text-white font-semibold p-3 rounded-lg hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
                                      >
                                        <Plus className="w-4 h-4 mr-2" /> إضافة مستخدم
                                      </button>
                                    </form>
                                  </div>
                                  
                                  {/* Project Users List */}
                                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                                    <h5 className="text-lg font-bold text-gray-700 mb-3 flex items-center">
                                      <Users className="w-5 h-5 mr-2 text-indigo-600" /> 
                                      المستخدمون في هذا المشروع ({projectUsersList.length})
                                    </h5>
                                    {projectUsersList.length > 0 ? (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {projectUsersList.map((pu) => (
                                          <div key={pu.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                                            <div className="flex items-center space-x-3">
                                              <div className={`w-3 h-3 rounded-full ${
                                                pu.project_role === 'admin' ? 'bg-blue-500' : 'bg-green-500'
                                              }`}></div>
                                              <span className="font-semibold text-gray-800">{pu.username}</span>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${
                                                pu.project_role === 'admin' 
                                                  ? 'bg-blue-100 text-blue-800' 
                                                  : 'bg-green-100 text-green-800'
                                              }`}>
                                                {pu.project_role === 'admin' ? '👤 مدير' : '🏪 بائع'}
                                              </span>
                                              <button
                                                onClick={() => handleRemoveUserFromProject(project.id, pu.id)}
                                                className="text-red-600 hover:text-red-800"
                                                title="إزالة"
                                              >
                                                <X className="w-4 h-4" />
                                              </button>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                                        <Users className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                                        <p className="text-sm text-gray-600 font-medium">لا يوجد مستخدمون في هذا المشروع</p>
                                        <p className="text-xs text-gray-500 mt-1">استخدم النموذج أعلاه لإضافة مستخدمين</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectsPage;

